import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { HeroIllustration } from "./HeroIllustration";

const randomBetween = (min, max) => Math.random() * (max - min) + min;

// Generates a random-looking, never-repeating drift path using Framer's own
// infinite repeat engine (keyframe arrays), so the animation runs
// independently of React re-renders or JS timers - it cannot silently stall.
const FloatingBlob = ({ className, range = 150 }) => {
  const [path] = useState(() => {
    const points = 5;
    const xs = [0];
    const ys = [0];
    const scales = [1];
    for (let i = 0; i < points; i++) {
      xs.push(randomBetween(-range, range));
      ys.push(randomBetween(-range, range));
      scales.push(randomBetween(0.75, 1.5));
    }
    xs.push(0);
    ys.push(0);
    scales.push(1);
    return {
      xs,
      ys,
      scales,
      duration: randomBetween(16, 30),
      delay: randomBetween(0, 3),
    };
  });

  return (
    <motion.div
      animate={{ x: path.xs, y: path.ys, scale: path.scales }}
      transition={{
        duration: path.duration,
        delay: path.delay,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      }}
      className={className}
    />
  );
};

export const HeroSection = () => {
  const scrollToServices = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative min-h-screen flex items-center pt-20 hero-gradient overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#003333]/[0.02] rounded-full blur-3xl"
        />

        {/* Colourful ambient light blobs - drift randomly, never the same loop twice */}
        <FloatingBlob
          range={160}
          className="absolute -top-10 left-[2%] w-[420px] h-[420px] rounded-full bg-[#66B3B3] opacity-50 blur-3xl"
        />
        <FloatingBlob
          range={180}
          className="absolute top-16 right-[8%] w-[380px] h-[380px] rounded-full bg-[#4F7FE0] opacity-40 blur-3xl"
        />
        <FloatingBlob
          range={140}
          className="absolute bottom-10 left-[28%] w-[340px] h-[340px] rounded-full bg-[#E066AD] opacity-30 blur-3xl"
        />
        <FloatingBlob
          range={170}
          className="absolute bottom-16 right-[2%] w-[300px] h-[300px] rounded-full bg-[#66B3B3] opacity-45 blur-3xl"
        />

        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 0.5, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#F8FAFC] to-transparent"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Left content */}
          <div className="order-2 md:order-1">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-6"
            >
              Custom Software Development · London, UK
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.9] text-[#003333] mb-8"
            >
              Systems
              <br />
              <span className="text-[#004D4D]">That Scale.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg md:text-xl leading-relaxed text-slate-600 mb-10 max-w-lg"
            >
              LOLCorp is a UK-based custom software development company. We
              design and build secure, scalable systems for growing
              businesses. 4-6 week projects starting at £10,000 GBP. <br /><br />We work with
              growing businesses that have outgrown Excel, email, and
              disconnected tools. We design and build custom internal systems
              that replace spreadsheets with clarity, control, and scalability —
              without forcing your operations into off-the-shelf software
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/consultation"
                className="group inline-flex items-center justify-center gap-2 bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 hover:scale-105"
                data-testid="hero-cta-consultation"
              >
                Book Consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>

              <button
                onClick={scrollToServices}
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#003333] text-[#003333] hover:bg-[#003333] hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
                data-testid="hero-cta-services"
              >
                Explore Services
              </button>
            </motion.div>
          </div>

          {/* Right content - Company Logo */}
          <motion.div
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="order-1 md:order-2 relative"
          >
            <HeroIllustration />
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.button
          onClick={scrollToServices}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center text-[#003333]/50 hover:text-[#003333] transition-colors"
          data-testid="scroll-indicator"
        >
          <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
          <ChevronDown className="w-5 h-5" />
        </motion.button>
      </motion.div>
    </section>
  );
};
